import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import i18n from 'i18n';
import apiCubic from 'config/api-cubic';
import requester from 'utils/requester';
import { INIT_UI_LANGUAGE } from 'constants/languages';
import { isPhoneValid } from 'utils/validator';
import KeyboardSmall from 'components/keyboard-small';
import {
  StyledModalContainer,
  StyledTitle,
  StyledDescription,
  StyledBlock,
  StyledInputInvalidMessage,
} from './StyledComponents';
import ActionsSection from './ActionsSection';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';

const code = '+380';

const PhoneModal = ({ contractNumber, onModalConfirm = noop, onModalBack = noop }) => {
  const [phone, setPhone] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [error, setError] = useState(null);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const value = code + phone;
  const cursorPosition = value.length;

  const showKeyboard = useCallback(() => setIsKeyboardVisible(true), []);
  const hideKeyboard = useCallback(() => {
    setTimeout(function () {
      SpatialNavigationWrapper.focus('@PHONE_MODAL');
    }, 100);
    setIsKeyboardVisible(false);
  }, []);

  const onKeyboardInput = useCallback(
    (value) => {
      setError(null);
      if (value === 'remove') {
        setPhone((prev) => prev.slice(0, -1));
      } else if (value === 'close') {
        setIsValid(isPhoneValid(phone));
        hideKeyboard();
      } else if (!isPhoneValid(phone)) {
        setPhone((prev) => prev + value);
      }
    },
    [phone, hideKeyboard],
  );

  const onSubmit = useCallback(async () => {
    if (!isPhoneValid(phone)) {
      setError(i18n.t('phoneModal.phoneInvalid'));
      return;
    }

    const authToken = apiCubic.getAuthToken();

    try {
      const data = await requester({
        method: 'post',
        url: `${apiCubic.host}${apiCubic.endpoints.phoneActivationOTP}`,
        appendParams: false,
        extendHeadersWithSession: false,
        headers: { Authorization: `Basic ${authToken}` },
        data: { contractCode: contractNumber, phone: `0${phone}`, language: INIT_UI_LANGUAGE },
      });

      innerAnalyticsVolia.postEvent({
        eventGroup: 'alternative sign in screen',
        eventName: 'click add phone',
        eventValue: data.error ? data.error.code : 'success',
        addInfo: `0${phone}`,
      });

      if (data.error) throw new Error(data.error.message);

      onModalConfirm({ phone });
    } catch (error) {
      console.error(error);
      setError(error.message);
    } finally {
    }
  }, [contractNumber, onModalConfirm, phone]);

  return (
    <StyledModalContainer>
      <StyledTitle style={{ minHeight: 0 }}>
        {i18n.t('phoneModal.title')}
        <StyledDescription>{i18n.t('phoneModal.description')}</StyledDescription>
      </StyledTitle>
      <StyledBlock>
        <ActionsSection
          sectionName="PHONE_MODAL"
          config={{
            leaveFor: {
              right: '',
              up: '',
              left: '',
              down: '',
            },
          }}
          noMargin
          defaultSection
        >
          <ActionsSection.Input
            value={value}
            isValid={!isKeyboardVisible ? isValid : true}
            invalidMessage={i18n.t('phoneModal.phoneInvalid')}
            cursor={isKeyboardVisible}
            cursorPosition={cursorPosition}
            onEnter={showKeyboard}
          />
          {error && <StyledInputInvalidMessage>{error}</StyledInputInvalidMessage>}
          <ActionsSection.Button label={i18n.t('phoneModal.submit')} onEnter={onSubmit} onFocus={hideKeyboard} />
          <ActionsSection.Button label={i18n.t('phoneModal.skip')} onEnter={onModalBack} onFocus={hideKeyboard} />
        </ActionsSection>
        {isKeyboardVisible && (
          <KeyboardSmall className="keyboard-small__phone" onEnter={onKeyboardInput} onLeave={hideKeyboard} />
        )}
      </StyledBlock>
    </StyledModalContainer>
  );
};

PhoneModal.propTypes = {
  contractNumber: PropTypes.string.isRequired,
  onModalConfirm: PropTypes.func.isRequired,
  onModalBack: PropTypes.func.isRequired,
};

export default PhoneModal;
